<template>
  <div
    class="sequence-detail-output overflow-y-scroll custom_scrollbar flex flex-col pr-4 gap-4"
  >
    <ViewFilterComponent
      source="sequence_emails"
      :filters-options="emailFiltersOptions"
      :filter-data="emailFilterData"
      @filter-changed="handleEmailFilterChange"
      :show-clear-all="true"
      @clear-all-filters="clearAllFilters"
    />

    <div class="flex flex-col">
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center gap-2 mt-20"
      >
        <CustomLoader :size="28" />
      </div>

      <EmailsTable
        v-else-if="emails.length > 0"
        :loading="loading"
        :pagination="pagination"
        :emails="emails"
        :handlePageChange="handlePageChange"
        :composeEmailReply="composeEmailReply"
        @show-activity="showEmailActivity"
        :source="entityTypes.SEQUENCE_EMAILS"
        :selection="selection"
        :emailFilterData="emailFilterDataWithSequence"
        @update:selection="handleSelectionUpdate"
      />

      <EmptyScreen
        v-else
        padding="py-36"
        source="sequence_emails"
        text="No emails found!"
        description="Start sending emails to see them here"
        :type="emptyScreenTypes.SECONDARY"
      />
    </div>

    <CustomDrawer
      title="Sent Email"
      v-model:model-value="showActivityModal"
      @update:modelValue="updateShowActivityModal"
      width="30vw"
      direction="right"
      v-if="messageDetails && activity"
    >
      <EmailActivity
        :message="messageDetails"
        :activity="activity"
        :engagement="engagement"
      />
    </CustomDrawer>

    <email-compose
      :visible="emailComposeVisible"
      :prefilled-email-data="currentEmailData"
      :is-reply="true"
      source="sequence_email_activity"
      @email-sent="onEmailSent"
      @update:visible="updateEmailComposeVisible"
      auto-focus
    />

    <ExportModal
      :visible="showExportModal"
      :title="`Export ${featureNames.SEQUENCES} Emails`"
      :description="`Selected ${featureNames.SEQUENCES.toLowerCase()} emails will be exported to your email`"
      :selection="selection"
      :export-function="
        (exportData) => EmailService.exportEmails(exportData, emailFilterDataWithSequence)
      "
      @update:visible="showExportModal = $event"
      @export-success="handleExportSuccess"
    />
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, computed } from "vue";
import { updateFilterOptions } from "@/src/utils/utils.js";
import { message } from "ant-design-vue";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import { sequenceEmailFilters } from "@/src/constants/filterConstants.js";
import debounce from "lodash/debounce";
import eventBus from "@/src/utils/eventBus.js";
import EmailService from "@/src/api/EmailService.js";
import SequenceService from "@/src/api/SequenceService.js";
import DashboardService from "@/src/api/DashboardService.js";
import CustomLoader from "@/src/components/CustomLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import EmailsTable from "@/src/components/emails/EmailsTable.vue";
import EmailActivity from "@/src/components/emails/EmailActivity.vue";
import EmailCompose from "@/src/views/email_template/EmailCompose.vue";
import ViewFilterComponent from "@/src/components/view_components/ViewFilterComponent.vue";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import ExportModal from "@/src/components/ExportModal.vue";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  sequenceId: { type: [String, Number], required: false },
});

const loading = ref(false);
const pagination = ref({
  current: 1,
  pageSize: 15,
  total: 0,
});

const selection = ref({
  selected_ids: [],
  unselected_ids: [],
  unselected_emails: [],
  select_all: false,
});

const showExportModal = ref(false);

const handleSelectionUpdate = (newSelection) => {
  selection.value = newSelection;
};

const handleExportSuccess = () => {
  selection.value = {
    selected_ids: [],
    unselected_ids: [],
    unselected_emails: [],
    select_all: false,
  };
};

const emailFiltersOptions = ref(sequenceEmailFilters);
const emailFilterData = ref({});
const emails = ref([]);

// Computed filter data that always includes the sequence ID for API calls
const emailFilterDataWithSequence = computed(() => ({
  ...emailFilterData.value,
  sequences: props.sequenceId
    ? [props.sequenceId]
    : emailFilterData.value?.sequences || [],
}));
const showActivityModal = ref(false);
const messageDetails = ref(null);
const activity = ref(null);
const engagement = ref(null);
const emailComposeVisible = ref(false);
const currentEmailData = ref(null);
const debouncedApplyFilters = debounce(() => {
  applyFilters();
}, 300);

const fetchSenders = async () => {
  if (props.sequenceId) {
    try {
      const response = await SequenceService.fetchSequenceEmailAccounts(props.sequenceId);
      
      if (response?.success) {
        const senders = response.email_accounts.map((email) => ({
          name: email.email,
          ...email,
        }));
        
        updateFilterOptions(emailFiltersOptions.value, "senders_email", senders);
      } else {
        console.error("Failed to fetch sequence email accounts:", response?.error);
        message.error("Failed to fetch senders. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching sequence email accounts:", error);
      message.error("Failed to fetch senders. Please try again.");
    }
    return;
  }

  const personalMailboxes = await DashboardService.fetchPersonalMailboxes();
  const sharedMailboxes = await DashboardService.fetchSharedMailboxes();

  let senders = [];
  if (personalMailboxes?.success) {
    senders = [
      ...personalMailboxes.email_accounts
        .filter((account) => account.status === "CONNECTED")
        .map((email) => ({
          name: email.email,
          ...email,
        })),
    ];
  } else {
    console.error("Failed to fetch senders:", personalMailboxes.errors);
    message.error("Failed to fetch senders. Please try again.");
  }

  if (sharedMailboxes?.success) {
    senders = [
      ...senders,
      ...sharedMailboxes.email_accounts
        .filter((account) => account.status === "CONNECTED")
        .map((email) => ({
          name: email.email,
          ...email,
        })),
    ];
  } else {
    console.error("Failed to fetch senders:", sharedMailboxes.errors);
    message.error("Failed to fetch senders. Please try again.");
  }

  updateFilterOptions(emailFiltersOptions.value, "senders_email", senders);
};

const applyFilters = async () => {
  loading.value = true;
  try {
    const filters = {
      ...emailFilterData.value,
      sequences: props.sequenceId
        ? [props.sequenceId]
        : emailFilterData.value?.sequences || [],
    };

    const response = await EmailService.fetchEmails(
      pagination.value.current,
      pagination.value.pageSize,
      filters,
    );

    if (response?.success) {
      emails.value = response.emails;
      pagination.value.total = response.total;
    } else {
      console.error("Failed to fetch emails:", response.errors);
      message.error("Failed to fetch emails. Please try again.");
    }
  } catch (error) {
    console.error("Error fetching emails:", error);
    message.error("An error occurred while fetching emails.");
  } finally {
    loading.value = false;
  }
};

const handleEmailFilterChange = async ({ filter, updated_values }) => {
  if (updated_values) {
    // Create a new object to ensure reactivity triggers prop updates
    emailFilterData.value = { ...emailFilterData.value, [filter]: updated_values };
  } else {
    // Create a new object without the filter key
    const { [filter]: _, ...rest } = emailFilterData.value;
    emailFilterData.value = rest;
  }
  debouncedApplyFilters();
};

const handlePageChange = async (page) => {
  pagination.value = page;
  await applyFilters();
};

const showEmailActivity = async (row) => {
  showActivityModal.value = true;
  if (!row || !row.id) {
    console.error("Invalid row data: missing id");
    return;
  }

  const response = await EmailService.fetchEmailActivity(row.id);
  if (response?.success) {
    messageDetails.value = response.message;
    activity.value = response.activity;
    engagement.value = row.engagement || null;
  } else {
    console.error("Failed to fetch email activity:", response.errors);
    message.error("Failed to fetch email activity. Please try again.");
  }
};

const updateShowActivityModal = (value) => {
  showActivityModal.value = value;
  if (!value) {
    messageDetails.value = null;
    activity.value = null;
  }
};

const updateEmailComposeVisible = (value) => {
  emailComposeVisible.value = value;
};

const onEmailSent = async () => {
  eventBus.emit("email-sent");
  updateEmailComposeVisible(false);
  currentEmailData.value = null;
  setTimeout(() => {
    applyFilters();
  }, 5000);
};

const composeEmailReply = (row, replyAll) => {
  const email = row["message"];

  replyAll = replyAll === undefined;

  let ccEmails = [];
  if (replyAll && email?.cc?.length > 0) {
    ccEmails = email.cc.map((con) => con.email);
  }

  const allEmails = [...new Set([...email.to.map((contact) => contact.email)])];
  const userEmailVal = email.user_email;

  let replyEmail = allEmails.filter((item) => item.email !== email.from.email);
  currentEmailData.value = {
    from: userEmailVal,
    to: replyEmail,
    cc: ccEmails,
    bcc: [],
    subject: `Re: ${email.subject}`,
    content: "",
    thread_id: email.thread_id,
    message_id: email.message_id,
    is_reply_all: replyAll,
    attachments: [],
    replyTo:
      email.from.email === userEmailVal ? userEmailVal : email.from.email,
    prev_message: email.message,
  };
  emailComposeVisible.value = true;
};

const handleGoToEntity = async (data) => {
  if (!data || !data.email) {
    console.log("No valid email found to search for");
    return;
  }

  try {
    const response = await DashboardService.searchLeadsContacts({
      search_query: data.email.trim(),
      contact_access: "true",
      lead_access: "true",
    });

    if (response.success && response.entities && response.entities.length > 0) {
      const entity = response.entities[0];
      const entityType =
        entity.entity_type === "CONTACT" ? "contacts" : "leads";
      const entityId = entity.id;

      const url = `${window.location.origin}/${entityType}/${entityId}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      const url = `${window.location.origin}/entity-invalid`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  } catch (error) {
    console.error("Error searching for entity:", error);
    message.error("Error searching for entity with the provided email");
  }
};

const clearAllFilters = () => {
  emailFilterData.value = {};
  debouncedApplyFilters();
};

eventBus.on("sequence-emails/export", () => {
  if (
    selection.value.selected_ids.length === 0 &&
    !selection.value.select_all
  ) {
    message.error("Please select emails to export");
    return;
  }

  showExportModal.value = true;
});

onMounted(() => {
  fetchSenders();
  applyFilters();
  eventBus.on("emails/showActivity", showEmailActivity);
  eventBus.on("emails/goToEntity", handleGoToEntity);
});

onBeforeUnmount(() => {
  eventBus.off("emails/showActivity");
  eventBus.off("emails/goToEntity");
  eventBus.off("sequence-emails/export");
});
</script>
